import { clsx, type ClassValue } from 'clsx'

/**
 * Merge class names conditionally
 */
export function cn(...inputs: ClassValue[]) {
    return clsx(inputs)
}

/**
 * Format currency based on locale
 */
export function formatCurrency(amount: number, currency: 'USD' | 'VND' = 'USD'): string {
    if (currency === 'VND') {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            maximumFractionDigits: 0,
        }).format(amount)
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(amount)
}

const DEFAULT_EXCHANGE_RATE = 27000

/**
 * Convert price from VND to target currency
 * All prices in DB are stored in VND
 * @param priceVND - Price in VND
 * @param currency - Target currency
 * @param exchangeRate - Optional custom exchange rate (VND per 1 USD)
 */
export function convertPrice(priceVND: number, currency: 'USD' | 'VND' = 'VND', exchangeRate: number = DEFAULT_EXCHANGE_RATE): number {
    if (currency === 'USD') {
        return priceVND / exchangeRate
    }
    return priceVND
}

/**
 * Format price with automatic conversion from VND
 * Use this for all price displays
 * @param priceVND - Price in VND
 * @param currency - Target currency
 * @param exchangeRate - Optional custom exchange rate
 */
export function formatPrice(priceVND: number, currency: 'USD' | 'VND' = 'VND', exchangeRate: number = DEFAULT_EXCHANGE_RATE): string {
    const converted = convertPrice(priceVND, currency, exchangeRate)
    return formatCurrency(converted, currency)
}

/**
 * Format date based on locale
 * Handles both Unix timestamps (seconds) and date strings
 */
export function formatDate(input: number | string, locale: string = 'en'): string {
    let date: Date

    if (typeof input === 'number') {
        // Unix timestamp in seconds
        date = new Date(input * 1000)
    } else if (typeof input === 'string') {
        // Date string - convert space separator to T for ISO compatibility
        date = new Date(input.replace(' ', 'T'))
    } else {
        date = new Date()
    }

    // Validate date
    if (isNaN(date.getTime())) {
        return locale === 'vi' ? 'Ngày không hợp lệ' : 'Invalid date'
    }

    return new Intl.DateTimeFormat(locale === 'vi' ? 'vi-VN' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(date)
}

/**
 * Format relative time
 */
export function formatRelativeTime(timestamp: number, locale: string = 'en'): string {
    const now = Date.now()
    const diff = now - timestamp * 1000
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 7) {
        return formatDate(timestamp, locale)
    }
    if (days > 0) {
        return locale === 'vi' ? `${days} ngày trước` : `${days}d ago`
    }
    if (hours > 0) {
        return locale === 'vi' ? `${hours} giờ trước` : `${hours}h ago`
    }
    if (minutes > 0) {
        return locale === 'vi' ? `${minutes} phút trước` : `${minutes}m ago`
    }
    return locale === 'vi' ? 'Vừa xong' : 'Just now'
}

/**
 * Calculate days between two dates
 * Handles both Date objects and date strings (from localStorage)
 */
export function daysBetween(start: Date | string, end: Date | string): number {
    const startDate = typeof start === 'string' ? new Date(start) : start
    const endDate = typeof end === 'string' ? new Date(end) : end
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
    return Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

/**
 * Validate Vietnamese phone number
 */
export function isValidPhone(phone: string): boolean {
    const phoneRegex = /^(\+84|84|0)[1-9]\d{8,9}$/
    return phoneRegex.test(phone.replace(/\s/g, ''))
}

/**
 * Generate URL-friendly slug
 */
export function slugify(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength - 3) + '...'
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null
    return (...args: Parameters<T>) => {
        if (timeout) clearTimeout(timeout)
        timeout = setTimeout(() => func(...args), wait)
    }
}

/**
 * Calculate owner badge level based on verification status
 */
export function getOwnerBadges(owner: {
    cccdVerified?: boolean
    phoneVerified?: boolean
    faceVerified?: boolean
    rating?: number
}): { verified: boolean; topChoice: boolean } {
    const verified = Boolean(owner.cccdVerified && owner.phoneVerified && owner.faceVerified)
    const topChoice = (owner.rating || 0) >= 4.5
    return { verified, topChoice }
}

/**
 * Calculate booking total with add-ons
 */
export function calculateBookingTotal(
    dailyRate: number,
    days: number,
    addons: { price: number; perDay?: boolean }[]
): number {
    const vehicleTotal = dailyRate * days
    const addonsTotal = addons.reduce((sum, addon) => {
        return sum + (addon.perDay ? addon.price * days : addon.price)
    }, 0)
    return vehicleTotal + addonsTotal
}

/**
 * Get status badge color
 */
export function getStatusColor(status: string): string {
    const colors: Record<string, string> = {
        active: 'bg-green-100 text-green-800',
        pending: 'bg-yellow-100 text-yellow-800',
        confirmed: 'bg-blue-100 text-blue-800',
        completed: 'bg-gray-100 text-gray-800',
        cancelled: 'bg-red-100 text-red-800',
        published: 'bg-green-100 text-green-800',
        draft: 'bg-gray-100 text-gray-800',
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Generate random ID
 */
export function generateId(): string {
    return Math.random().toString(36).substring(2, 15)
}
